import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  mergeDocuments,
  getMergeJobStatus,
  useMergeJobSubscription,
  ShipmentDocument,
} from '@wove/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  JobProgress,
  Alert,
  Badge,
  cn,
} from '@wove/web-ui'
import { GitMerge, CheckCircle, AlertCircle, FileText } from 'lucide-react'

interface MergeDocumentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shipmentId: string
  documentIds: string[]
  documents: ShipmentDocument[]
}

export const MergeDocumentsDialog: React.FC<MergeDocumentsDialogProps> = ({
  open,
  onOpenChange,
  shipmentId,
  documentIds,
  documents,
}) => {
  const queryClient = useQueryClient()
  const [jobId, setJobId] = useState<string | null>(null)
  const [pollingJobStatus, setPollingJobStatus] = useState<any>(null)

  // Subscribe to job updates
  const { jobStatus, isCompleted, isFailed } = useMergeJobSubscription(jobId || undefined)

  // Mutation to start merge job
  const startJobMutation = useMutation({
    mutationFn: () => mergeDocuments(shipmentId, documentIds),
    onSuccess: (data) => {
      if (data.success && data.jobId) {
        setJobId(data.jobId)
      }
    },
  })

  // Poll for job status if subscription fails
  useEffect(() => {
    if (!jobId || jobStatus) return

    const pollStatus = async () => {
      try {
        const result = await getMergeJobStatus(shipmentId, jobId)
        if (result.success) {
          setPollingJobStatus(result.job)

          // Refresh documents when merge completes
          if (result.job.status === 'completed') {
            queryClient.invalidateQueries({ queryKey: ['shipment', shipmentId] })
            queryClient.invalidateQueries({ queryKey: ['shipments', shipmentId, 'documents'] })
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error)
      }
    }

    // Initial poll
    pollStatus()

    // Set up polling interval
    const interval = setInterval(pollStatus, 2000)

    return () => clearInterval(interval)
  }, [jobId, jobStatus, shipmentId, queryClient])

  // Use subscription status if available, otherwise use polling status
  const currentStatus = jobStatus || pollingJobStatus

  // Start job when dialog opens
  useEffect(() => {
    if (open && !jobId && !startJobMutation.isPending) {
      startJobMutation.mutate()
    }
  }, [open])

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setJobId(null)
      setPollingJobStatus(null)
      startJobMutation.reset()
    }
  }, [open])

  const selectedDocs = documents.filter((doc) => documentIds.includes(doc.id))

  const renderResults = () => {
    if (!currentStatus?.result) return null

    const result = currentStatus.result
    const mergedFields = result.mergedFields || {}
    const conflicts = result.conflicts || []

    return (
      <div className="space-y-4 mt-4">
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <div>
            <strong>Merge completed successfully!</strong>
            <p className="text-sm mt-1">
              {Object.keys(mergedFields).length} fields merged from {selectedDocs.length} documents
            </p>
          </div>
        </Alert>

        {conflicts.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              Conflicts Resolved ({conflicts.length})
            </h4>
            <div className="space-y-2">
              {conflicts.map((conflict: any, index: number) => (
                <div key={index} className="rounded-md border p-3 text-sm">
                  <p className="font-medium">{conflict.field}</p>
                  <p className="text-muted-foreground mt-1">
                    Resolution: Used value from {conflict.source}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-md bg-muted p-3">
          <p className="text-sm text-muted-foreground">
            The merged document has been created and is now available in your documents list.
          </p>
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5" />
            Merge Documents
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selected documents */}
          <div>
            <h4 className="text-sm font-medium mb-2">Documents to Merge ({selectedDocs.length})</h4>
            <div className="space-y-1">
              {selectedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>{doc.name}</span>
                  <Badge variant="secondary">{doc.type}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Job progress */}
          {(currentStatus || startJobMutation.isPending) && (
            <JobProgress
              status={startJobMutation.isPending ? 'pending' : currentStatus?.status || 'pending'}
              progress={currentStatus?.progress || 0}
              error={currentStatus?.error}
              title="Merging documents"
              subtitle="Combining data from selected documents into a single document"
            />
          )}

          {/* Results */}
          {currentStatus?.status === 'completed' && renderResults()}

          {/* Error */}
          {startJobMutation.error && (
            <Alert variant="error">
              <AlertCircle className="h-4 w-4" />
              <div>
                <strong>Failed to start merge</strong>
                <p className="text-sm mt-1">{(startJobMutation.error as any)?.message}</p>
              </div>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {currentStatus?.status === 'completed' || currentStatus?.status === 'failed'
              ? 'Close'
              : 'Cancel'}
          </Button>
          {currentStatus?.status === 'failed' && (
            <Button onClick={() => startJobMutation.mutate()}>Retry</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
